import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ARMcontext } from '../../utils/context/context';
import { Teams } from '../../utils/api';
import type {
  DashboardActivityAlertLevel,
  DashboardActivityRow,
  DashboardActivityTableSortColumn,
  DashboardActivityTableSortDirection,
} from '../../interfaces/dashboardLeaderboardActivity';
import {
  DEFAULT_ACTIVITY_PAGE_SIZE,
  useDashboardActivityTable,
} from '../../utils/hooks/useDashboardActivityTable.hook';
import PaginationControls from '../PaginationControls/PaginationControls';
import {
  ActivityTableCard,
  ActivityTableHeader,
  ActivityTableTitleRow,
  ActivityTableTitle,
  ActivityInfoButton,
  ActivityTableScroll,
  ActivityTableEl,
  ActivityThGroup,
  ActivityThPlayerRowspan,
  ActivityThDailySortable,
  ActivityThFreshSortable,
  ActivityThPost,
  ActivityThPostSortable,
  ActivityThTrainingSortable,
  ActivityTd,
  ActivityTdDaily,
  ActivityTdFresh,
  ActivityTdPost,
  ActivityTdTraining,
  ActivityTdPlayer,
  ActivityPlayerNameButton,
  ActivityFooterSummary,
  ActivityTableBelowCard,
  ActivityAlertPill,
  ActivityThSortIconSlot,
} from './dashboardActivityTableStyles';
import { ActivityTableSortChevron } from './ActivityTableSortChevron';
import {
  MetricInfoTooltipWrap,
  MetricTooltipBubble,
} from './sectionStyles';

/**
 * Formats an ISO date string as MM/DD/YY for dense table cells.
 * @returns Em dash when missing or invalid.
 */
function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

/** Stringifies a numeric cell value or returns an em dash when null/undefined. */
function cellNum(n: number | null | undefined): string {
  return n == null ? '—' : String(n);
}

/** Human-readable label for an alert pill (`no alerts` maps to “No alerts”). */
function alertLabel(level: DashboardActivityAlertLevel): string {
  if (level == null) return '—';
  if (level === 'no alerts') return 'No alerts';
  return level;
}

/**
 * Maps internal sort state to `aria-sort` for sortable column headers.
 * @returns `undefined` when the column is not the active sort column.
 */
function ariaSortForColumn(
  column: DashboardActivityTableSortColumn,
  sortColumn: DashboardActivityTableSortColumn | null,
  sortDirection: DashboardActivityTableSortDirection,
): 'ascending' | 'descending' | undefined {
  if (sortColumn !== column) return undefined;
  return sortDirection === 'asc' ? 'ascending' : 'descending';
}

/** Renders the dual-chevron sort control in a fixed-width slot for a header cell. */
function SortChevronSlot({
  column,
  sortColumn,
  sortDirection,
  onAscClick,
  onDescClick,
}: {
  column: DashboardActivityTableSortColumn;
  sortColumn: DashboardActivityTableSortColumn | null;
  sortDirection: DashboardActivityTableSortDirection;
  onAscClick: () => void;
  onDescClick: () => void;
}) {
  const direction = sortColumn === column ? sortDirection : null;
  return (
    <ActivityThSortIconSlot>
      <ActivityTableSortChevron
        direction={direction}
        onAscClick={onAscClick}
        onDescClick={onDescClick}
      />
    </ActivityThSortIconSlot>
  );
}

/** Renders an alert level as a pill or em dash when absent. Optional `Td` override for column styling. */
function AlertCell({
  level,
  Td = ActivityTd,
}: {
  level: DashboardActivityAlertLevel;
  Td?: React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
}) {
  const Cell = Td;
  if (level == null) return <Cell>—</Cell>;
  return (
    <Cell>
      <ActivityAlertPill $level={level}>{alertLabel(level)}</ActivityAlertPill>
    </Cell>
  );
}

type DashboardActivityTableProps = {
  /** Position / roster filter (mapped to API `position` when applicable). */
  playersGroup?: string;
  playerNameSearch?: string;
};

/**
 * Full-width activity table: Daily Check-In, Fresh/Post alerts, Training columns with server sort and pagination.
 * Loads via {@link useDashboardActivityTable}; player name opens Key Metrics for that player.
 *
 * @param playersGroup - Roster filter; mapped to API `position` when applicable.
 * @param playerNameSearch - Passed to API as `search` (Players page search bar).
 */
export function DashboardActivityTable({ playersGroup, playerNameSearch }: DashboardActivityTableProps) {
  const { selectedTeam, HandleSelectedPlayer } = useContext(ARMcontext);
  const navigate = useNavigate();
  const teamsApi = useMemo(() => new Teams(), []);
  const teamId = (selectedTeam as any)?._id || (selectedTeam as any)?.id || null;

  const [page, setPage] = useState(1);
  const [openingPlayerId, setOpeningPlayerId] = useState<string | null>(null);
  const openingPlayerRequestRef = useRef(false);
  const [pageSize, setPageSize] = useState(DEFAULT_ACTIVITY_PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<DashboardActivityTableSortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<DashboardActivityTableSortDirection>('desc');

  /**
   * Header click / Enter+Space: cycles sort for that column (none → asc → desc → none).
   * Unsorted omits sort query params on the activity API.
   */
  const handleSort = (column: DashboardActivityTableSortColumn) => {
    // Cycle sort state:
    // 1) unsorted (sortColumn === null) -> asc
    // 2) asc -> desc
    // 3) desc -> unsorted (clear sort column so query param omits sorting)
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }

    // sortDirection === 'desc'
    setSortColumn(null);
    setSortDirection('desc');
  };

  /** Chevron control: request ascending sort, or clear sort if this column is already asc. */
  const handleSortAscClick = (column: DashboardActivityTableSortColumn) => {
    if (sortColumn === column && sortDirection === 'asc') {
      setSortColumn(null);
      setSortDirection('desc');
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  /** Chevron control: request descending sort, or clear sort if this column is already desc. */
  const handleSortDescClick = (column: DashboardActivityTableSortColumn) => {
    if (sortColumn === column && sortDirection === 'desc') {
      setSortColumn(null);
      setSortDirection('desc');
      return;
    }
    setSortColumn(column);
    setSortDirection('desc');
  };

  /** Combines column title markup with {@link SortChevronSlot} for sortable headers. */
  const renderSortHeaderContent = (
    title: React.ReactNode,
    column: DashboardActivityTableSortColumn,
  ) => (
    <span className="activity-th-sort-content">
      <span className="activity-sort-title">{title}</span>
      <SortChevronSlot
        column={column}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onAscClick={() => handleSortAscClick(column)}
        onDescClick={() => handleSortDescClick(column)}
      />
    </span>
  );

  useEffect(() => {
    setPage(1);
  }, [teamId, playersGroup, playerNameSearch, sortColumn, sortDirection]);

  const { data, isLoading, isError, isFetching } = useDashboardActivityTable({
    page,
    pageSize,
    teamId,
    playersGroup: playersGroup ?? '',
    search: playerNameSearch ?? '',
    sortColumn,
    sortDirection,
  });

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const rows = data?.rows ?? [];
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  /** Fetches player detail, updates ARM context, navigates to team Key Metrics (strength). */
  const openPlayerKeyMetrics = useCallback(
    async (playerId: string) => {
      if (openingPlayerRequestRef.current) return;
      openingPlayerRequestRef.current = true;
      setOpeningPlayerId(playerId);
      try {
        const playerInfo = await teamsApi.FetchSelectedPlayerInfo(playerId);
        if (playerInfo.success) {
          HandleSelectedPlayer(playerInfo.data);
          navigate('/players/keyMetrics/strength');
        }
      } finally {
        openingPlayerRequestRef.current = false;
        setOpeningPlayerId(null);
      }
    },
    [HandleSelectedPlayer, navigate, teamsApi],
  );

  /** Renders `<tr>` cells for daily, fresh, post, and training columns. */
  const renderRow = (row: DashboardActivityRow) => {
    const c = row.dailyCheckIn;
    const f = row.freshAlerts;
    const p = row.postAlerts;
    return (
      <tr key={row.playerId}>
        <ActivityTdPlayer>
          <ActivityPlayerNameButton
            type="button"
            title={row.playerName}
            aria-label={`Open Key Metrics for ${row.playerName}`}
            disabled={openingPlayerId !== null}
            onClick={() => void openPlayerKeyMetrics(row.playerId)}
          >
            {row.playerName}
          </ActivityPlayerNameButton>
        </ActivityTdPlayer>
        <ActivityTdDaily>{formatShortDate(c?.date)}</ActivityTdDaily>
        <ActivityTdDaily>{cellNum(c?.armFeels)}</ActivityTdDaily>
        <ActivityTdDaily>{cellNum(c?.bodyFeels)}</ActivityTdDaily>
        <ActivityTdDaily>{cellNum(c?.sleepQuality)}</ActivityTdDaily>
        <ActivityTdFresh>{formatShortDate(f?.lastFreshDate)}</ActivityTdFresh>
        <AlertCell level={f?.strength ?? null} Td={ActivityTdFresh} />
        <AlertCell level={f?.recovery ?? null} Td={ActivityTdFresh} />
        <ActivityTdPost>{formatShortDate(p?.lastPostDate)}</ActivityTdPost>
        <AlertCell level={p?.fatigue ?? null} Td={ActivityTdPost} />
        <ActivityTdPost>{p?.activityType ?? '—'}</ActivityTdPost>
        <ActivityTdPost>{cellNum(p?.rpe)}</ActivityTdPost>
        <ActivityTdPost>{cellNum(p?.count)}</ActivityTdPost>
        <ActivityTdTraining>{cellNum(row.trainingSessions)}</ActivityTdTraining>
      </tr>
    );
  };

  return (
    <>
    <ActivityTableCard>
      <ActivityTableHeader>
        <ActivityTableTitleRow>
          <ActivityTableTitle>Activity</ActivityTableTitle>
          <MetricInfoTooltipWrap>
            <ActivityInfoButton aria-label="Activity table info">
              i
            </ActivityInfoButton>
            <MetricTooltipBubble role="tooltip">
              Displays each player&apos;s most recent activity across Daily Check-In, Alerts, and
              Training. Each section reflects the latest available data independently, so dates may
              vary between columns.
            </MetricTooltipBubble>
          </MetricInfoTooltipWrap>
        </ActivityTableTitleRow>
      </ActivityTableHeader>

      <ActivityTableScroll>
        <ActivityTableEl>
          <thead>
            <tr>
              <ActivityThPlayerRowspan
                rowSpan={2}
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('playerName', sortColumn, sortDirection)}
                onClick={() => handleSort('playerName')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('playerName');
                  }
                }}
              >
                <span className="activity-th-player-inner">
                  {renderSortHeaderContent('Player name', 'playerName')}
                </span>
              </ActivityThPlayerRowspan>
              <ActivityThGroup colSpan={4} $tone="blue">
                Daily check in
              </ActivityThGroup>
              <ActivityThGroup colSpan={3} $tone="orange">
                Fresh alerts
              </ActivityThGroup>
              <ActivityThGroup colSpan={5} $tone="pink">
                Post alerts
              </ActivityThGroup>
              <ActivityThGroup colSpan={1} $tone="green">
                Training
              </ActivityThGroup>
            </tr>
            <tr>
              <ActivityThDailySortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('dailyDate', sortColumn, sortDirection)}
                onClick={() => handleSort('dailyDate')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('dailyDate');
                  }
                }}
              >
                {renderSortHeaderContent('Date', 'dailyDate')}
              </ActivityThDailySortable>
              <ActivityThDailySortable
                className="activity-th-left-title"
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('armFeels', sortColumn, sortDirection)}
                onClick={() => handleSort('armFeels')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('armFeels');
                  }
                }}
              >
                {renderSortHeaderContent(
                  <>
                    Arm
                    <br />
                    Feels
                  </>,
                  'armFeels',
                )}
              </ActivityThDailySortable>
              <ActivityThDailySortable
                className="activity-th-left-title"
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('bodyFeels', sortColumn, sortDirection)}
                onClick={() => handleSort('bodyFeels')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('bodyFeels');
                  }
                }}
              >
                {renderSortHeaderContent(
                  <>
                    Body
                    <br />
                    Feels
                  </>,
                  'bodyFeels',
                )}
              </ActivityThDailySortable>
              <ActivityThDailySortable
                className="activity-th-left-title"
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('sleepQuality', sortColumn, sortDirection)}
                onClick={() => handleSort('sleepQuality')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('sleepQuality');
                  }
                }}
              >
                {renderSortHeaderContent(
                  <>
                    Sleep
                    <br />
                    Quality
                  </>,
                  'sleepQuality',
                )}
              </ActivityThDailySortable>
              <ActivityThFreshSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('freshDate', sortColumn, sortDirection)}
                onClick={() => handleSort('freshDate')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('freshDate');
                  }
                }}
              >
                {renderSortHeaderContent('Date', 'freshDate')}
              </ActivityThFreshSortable>
              <ActivityThFreshSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('strength', sortColumn, sortDirection)}
                onClick={() => handleSort('strength')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('strength');
                  }
                }}
              >
                {renderSortHeaderContent('Strength', 'strength')}
              </ActivityThFreshSortable>
              <ActivityThFreshSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('recovery', sortColumn, sortDirection)}
                onClick={() => handleSort('recovery')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('recovery');
                  }
                }}
              >
                {renderSortHeaderContent('Recovery', 'recovery')}
              </ActivityThFreshSortable>
              <ActivityThPostSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('postDate', sortColumn, sortDirection)}
                onClick={() => handleSort('postDate')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('postDate');
                  }
                }}
              >
                {renderSortHeaderContent('Date', 'postDate')}
              </ActivityThPostSortable>
              <ActivityThPostSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('fatigue', sortColumn, sortDirection)}
                onClick={() => handleSort('fatigue')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('fatigue');
                  }
                }}
              >
                {renderSortHeaderContent('Fatigue', 'fatigue')}
              </ActivityThPostSortable>
              <ActivityThPostSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('type', sortColumn, sortDirection)}
                onClick={() => handleSort('type')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('type');
                  }
                }}
              >
                {renderSortHeaderContent('Type', 'type')}
              </ActivityThPostSortable>
              <ActivityThPostSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('rpe', sortColumn, sortDirection)}
                onClick={() => handleSort('rpe')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('rpe');
                  }
                }}
              >
                {renderSortHeaderContent('RPE', 'rpe')}
              </ActivityThPostSortable>
              <ActivityThPostSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('count', sortColumn, sortDirection)}
                onClick={() => handleSort('count')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('count');
                  }
                }}
              >
                {renderSortHeaderContent('Count', 'count')}
              </ActivityThPostSortable>
              <ActivityThTrainingSortable
                scope="col"
                tabIndex={0}
                aria-sort={ariaSortForColumn('trainingSessions', sortColumn, sortDirection)}
                onClick={() => handleSort('trainingSessions')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('trainingSessions');
                  }
                }}
              >
                {renderSortHeaderContent('Sessions', 'trainingSessions')}
              </ActivityThTrainingSortable>
            </tr>
          </thead>
          <tbody>
            {isError && (
              <tr>
                <ActivityTd colSpan={14} style={{ textAlign: 'center', padding: '24px' }}>
                  Unable to load activity.
                </ActivityTd>
              </tr>
            )}
            {isLoading && !data && !isError && (
              <tr>
                <ActivityTd colSpan={14} style={{ textAlign: 'center', padding: '24px' }}>
                  Loading…
                </ActivityTd>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <ActivityTd colSpan={14} style={{ textAlign: 'center', padding: '24px' }}>
                  No activity for this team.
                </ActivityTd>
              </tr>
            )}
            {!isError && rows.map(renderRow)}
          </tbody>
        </ActivityTableEl>
      </ActivityTableScroll>
    </ActivityTableCard>

    <ActivityTableBelowCard>
      <ActivityFooterSummary>
        {isFetching && !isLoading ? (
          <>Updating…</>
        ) : isLoading && !data ? (
          <>Loading…</>
        ) : isError ? (
          <>Could not load activities</>
        ) : totalCount === 0 ? (
          <>Showing 0 of 0 activities</>
        ) : (
          <>
            Showing {from}–{to} of {totalCount} activities
          </>
        )}
      </ActivityFooterSummary>
      {!isLoading && !isError && totalCount > 0 && (
        <PaginationControls
          totalItems={totalCount}
          currentPage={page}
          recordsPerPage={pageSize}
          onPageChange={setPage}
          onLimitChange={limit => {
            setPageSize(limit);
            setPage(1);
          }}
          totalPages={totalPages}
        />
      )}
    </ActivityTableBelowCard>
    </>
  );
}
